import { loadYouTubeApi } from '../embed-sdk.loader';
import { EmbedAdapterOptions, EmbedPlayerEvent, EmbedVideoAdapter } from '../embed-video.model';

declare const window: any;

/**
 * Builds the `<iframe>` itself (`enablejsapi=1` + `origin`) and attaches `YT.Player` to it, instead
 * of letting the SDK create its own — the iframe is a fresh element per mount (no fixed `id` the way
 * `tema.page.ts`'s `<div id="player">` is), so two instances never collide and destroying one never
 * touches another.
 */
export class YoutubeEmbedAdapter implements EmbedVideoAdapter {
  private player: any = null;
  private iframe: HTMLIFrameElement | null = null;
  private destroyed = false;

  mount(host: HTMLElement, options: EmbedAdapterOptions): void {
    const iframe = document.createElement('iframe');
    iframe.id = options.playerId;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    const origin = encodeURIComponent(window.location.origin);
    iframe.src =
      `https://www.youtube.com/embed/${options.videoId}` +
      `?enablejsapi=1&origin=${origin}&playsinline=1&rel=0&modestbranding=1&fs=1&controls=1`;
    host.appendChild(iframe);
    this.iframe = iframe;

    loadYouTubeApi().then(() => {
      if (this.destroyed) return;
      this.player = new window.YT.Player(iframe, {
        events: {
          onReady: () => {
            if (options.startAt > 0) this.player.seekTo(options.startAt, true);
            options.onEvent({ type: 'ready' });
          },
          onStateChange: (event: any) => this.handleStateChange(event, options),
          onError: (event: any) => options.onEvent({ type: 'error', error: event?.data }),
        },
      });
    });
  }

  private handleStateChange(event: any, options: EmbedAdapterOptions): void {
    const playerEvent = this.toPlayerEvent(event);
    if (playerEvent) options.onEvent(playerEvent);
  }

  private toPlayerEvent(event: any): EmbedPlayerEvent | null {
    const YT = window.YT;
    switch (event.data) {
      case YT.PlayerState.PLAYING:
        return { type: 'playing' };
      case YT.PlayerState.PAUSED:
        return { type: 'paused', currentTime: this.getCurrentTime() };
      case YT.PlayerState.ENDED:
        return { type: 'ended', currentTime: this.getDuration() };
      default:
        return null;
    }
  }

  getCurrentTime(): number {
    try {
      return this.player?.getCurrentTime?.() ?? 0;
    } catch {
      return 0;
    }
  }

  getDuration(): number {
    try {
      return this.player?.getDuration?.() ?? 0;
    } catch {
      return 0;
    }
  }

  seekTo(seconds: number): void {
    this.player?.seekTo?.(seconds, true);
  }

  play(): void {
    this.player?.playVideo?.();
  }

  pause(): void {
    this.player?.pauseVideo?.();
  }

  destroy(): void {
    this.destroyed = true;
    try {
      this.player?.destroy?.();
    } catch {
      /* already gone */
    }
    this.player = null;
    this.iframe?.remove();
    this.iframe = null;
  }
}
